import { useForm } from "@inertiajs/react";
import { Check, Edit3, Lock, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type EditableValue = string | number | null | undefined;

interface InlineEditableAsyncComboboxProps {
  label: string;
  value: string | number | null | undefined;
  field: string;
  updateUrl: string;
  icon?: React.ElementType;
  placeholder?: string;
  disabled?: boolean;
  formatValue?: (value: string | number | null | undefined) => string;
  searchEndpoint: string;
  refreshKeys?: string[];
}

const defaultFormatValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return "No asignado";
  return String(value);
};

export default function InlineEditableAsyncCombobox({
  label,
  value,
  field,
  updateUrl,
  icon: Icon,
  placeholder = "Seleccionar...",
  disabled = false,
  formatValue = defaultFormatValue,
  searchEndpoint,
  refreshKeys,
}: InlineEditableAsyncComboboxProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedOption, setSelectedOption] = useState<AsyncOption | null>(null);

  const { setData, patch, processing, errors, reset } = useForm<
    Record<string, EditableValue>
  >({
    [field]: value,
  });

  const startEditing = () => {
    if (!disabled) {
      reset();
      setData(field, value);
      setIsEditing(true);
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setSelectedOption(null);
    reset();
    setData(field, value);
  };

  const saveChanges = () => {
    patch(updateUrl, {
      preserveScroll: true,
      onSuccess: () => {
        setIsEditing(false);
        setSelectedOption(null);
        toast.success(`${label} actualizado`);
      },
      onError: () => {
        // Keep editing mode open if there are errors
      },
      ...(refreshKeys && refreshKeys.length > 0
        ? { only: refreshKeys }
        : {}),
    });
  };

  const loadOptions = async (inputValue: string): Promise<AsyncOption[]> => {
    const params = new URLSearchParams();
    params.set("term", inputValue || "");

    try {
      const res = await fetch(`${searchEndpoint}?${params.toString()}`);
      if (!res.ok) return [];
      const data = (await res.json()) as { value: number | string; label: string }[];
      return data.map((d) => ({ value: d.value, label: d.label }));
    } catch {
      return [];
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          {Icon && <Icon className="h-4 w-4" />}
          {label}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <AsyncCombobox
              value={selectedOption}
              onChange={(opt) => {
                setSelectedOption(opt);
                setData(field, opt?.value ?? null);
              }}
              defaultOptions={true}
              cacheOptions={true}
              loadOptions={loadOptions}
              placeholder={placeholder}
            />
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="default"
              onClick={saveChanges}
              disabled={processing}
              className="h-9 w-9 p-0"
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={cancelEditing}
              disabled={processing}
              className="h-9 w-9 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {errors[field] && (
          <p className="text-xs text-red-500">{errors[field]}</p>
        )}
      </div>
    );
  }

  // Display mode
  const displayValue = formatValue(value);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </div>
      <div
        className={[
          "flex items-center justify-between gap-2 min-w-0 rounded-md px-2 py-1 -mx-2 focus:outline-none",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/40",
          disabled
            ? "cursor-not-allowed bg-muted/40 text-muted-foreground"
            : "cursor-pointer hover:bg-muted/40 focus-within:bg-muted/40",
        ].join(" ")}
        onClick={() => {
          if (!disabled) startEditing();
        }}
        role={!disabled ? "button" : undefined}
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            startEditing();
          }
        }}
      >
        <p className="font-medium truncate min-w-0" title={displayValue}>
          {displayValue}
        </p>
        {disabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex h-8 w-8 items-center justify-center text-muted-foreground">
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            </TooltipTrigger>
            <TooltipContent>Este campo no se puede editar</TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  startEditing();
                }}
                aria-label={`Editar ${label}`}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              >
                <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Haz clic para editar</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
